import React, { useState, useEffect, useRef } from 'react';
import { Filter, Calendar, RefreshCw, User, MapPin, Check, ChevronDown } from 'lucide-react';

/**
 * Date input with mm/dd/yyyy mask. Stores ISO (yyyy-mm-dd) internally.
 */
function DateInput({ value, onChange, placeholder = 'mm/dd/yyyy' }) {
  const isoToDisplay = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${m}/${d}/${y}` : '';
  };
  const displayToIso = (display) => {
    const clean = display.replace(/[^\d/]/g, '');
    const parts = clean.split('/');
    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
      const m = parseInt(parts[0], 10);
      const d = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${parts[2]}-${parts[0]}-${parts[1]}`;
      }
    }
    return '';
  };

  const [text, setText] = useState(isoToDisplay(value));
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      setText(isoToDisplay(value));
      prevValue.current = value;
    }
  }, [value]);

  const handleChange = (e) => {
    let raw = e.target.value.replace(/[^\d]/g, '');
    if (raw.length > 4) raw = raw.slice(0, 2) + '/' + raw.slice(2, 4) + '/' + raw.slice(4, 8);
    else if (raw.length > 2) raw = raw.slice(0, 2) + '/' + raw.slice(2);
    setText(raw);
    const iso = displayToIso(raw);
    if (iso) { onChange(iso); prevValue.current = iso; }
    else if (raw === '') { onChange(''); prevValue.current = ''; }
  };

  return (
    <input
      type="text" inputMode="numeric" maxLength={10} value={text}
      onChange={handleChange} placeholder={placeholder}
      className="input-field w-full" data-testid="date-input"
    />
  );
}

/**
 * Multi-select checkbox dropdown
 */
function MultiSelectCheckbox({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter(v => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const selectAll = () => onChange(options.map(o => o.value));
  const clearAll = () => onChange([]);

  const selectedLabels = selected.length === 0
    ? `All ${label}`
    : selected.length === options.length
      ? `All ${label}`
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="input-field w-full flex items-center justify-between text-left text-sm"
        data-testid={`${testId}-trigger`}
      >
        <span className={selected.length === 0 ? 'text-slate-500' : 'text-slate-900'}>
          {selectedLabels}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {/* Quick actions */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
            <button onClick={selectAll} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">Select All</button>
            <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-700 font-medium">Clear</button>
          </div>
          {options.map(opt => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
                data-testid={`${testId}-option-${opt.value}`}
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  checked ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-300'
                }`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-slate-700">{opt.label}</span>
                <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggle(opt.value)} />
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'pending_cfo_approval', label: 'Pending CFO Approval' },
  { value: 'pending_ceo_approval', label: 'Pending CEO Approval' },
  { value: 'pending_admin_approval', label: 'Pending Admin Approval' },
  { value: 'partially_approved', label: 'Partially Approved' },
  { value: 'approved', label: 'Approved (Scenario)' },
  { value: 'fully_approved', label: 'Fully Approved (Proposal)' },
  { value: 'rejected', label: 'Rejected (Scenario)' },
  { value: 'fully_denied', label: 'Fully Denied (Proposal)' },
];

export default function AnalyticsFilters({
  filters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  filterOptions,
  userRole
}) {
  const handlePeriodClick = (period) => {
    onFilterChange('period', period);
  };

  // Status is now an array
  const selectedStatuses = filters.status ? filters.status.split(',').filter(Boolean) : [];

  const handleStatusChange = (newStatuses) => {
    onFilterChange('status', newStatuses.join(','));
  };

  // Build service options for multi-select
  const serviceOptions = (filterOptions.services || []).map(s => ({ value: s.id, label: s.name }));
  const selectedServices = filters.serviceId ? filters.serviceId.split(',').filter(Boolean) : [];
  const handleServiceChange = (newServices) => {
    onFilterChange('serviceId', newServices.join(','));
  };

  // Build AE options for multi-select
  const aeOptions = (filterOptions.account_executives || []).map(ae => ({ value: ae.id, label: ae.name }));
  const selectedAEs = filters.accountExecutive ? filters.accountExecutive.split(',').filter(Boolean) : [];
  const handleAEChange = (newAEs) => {
    onFilterChange('accountExecutive', newAEs.join(','));
  };

  // State options
  const stateOptions = (filterOptions.states || []).map(s => ({ value: s, label: s }));
  const selectedStates = filters.state ? filters.state.split(',').filter(Boolean) : [];
  const handleStateChange = (newStates) => {
    onFilterChange('state', newStates.join(','));
  };

  return (
    <div className="glass-card p-4 mb-6 relative z-30" style={{ overflow: 'visible' }} data-testid="analytics-filters">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-5 h-5 text-slate-600" />
        <h2 className="text-lg font-semibold text-slate-900">Filters & Reporting Periods</h2>
      </div>

      {/* Time Period Quick Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Time Period Analysis
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'custom', label: 'Custom' },
            { value: 'wtd', label: 'WTD' },
            { value: 'last_week', label: 'Last Week' },
            { value: 'last_4_weeks', label: 'Last 4 Weeks' },
            { value: 'mtd', label: 'MTD' },
            { value: 'qtd', label: 'QTD' },
            { value: 'ytd', label: 'YTD' },
            { value: 'ltm', label: 'LTM' },
            { value: 'wow', label: 'WoW' },
            { value: 'yoy', label: 'YoY' },
            { value: 'mom', label: 'MoM' }
          ].map(period => (
            <button
              key={period.value}
              onClick={() => handlePeriodClick(period.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filters.period === period.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
        {filters.period === 'custom' && (
          <p className="text-xs text-slate-500 mt-2">Custom period selected - use date pickers below to set range</p>
        )}
      </div>

      {/* Date Range and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Start Date
          </label>
          <DateInput
            value={filters.startDate}
            onChange={(isoVal) => onFilterChange('startDate', isoVal)}
            placeholder="mm/dd/yyyy"
          />
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            End Date
          </label>
          <DateInput
            value={filters.endDate}
            onChange={(isoVal) => onFilterChange('endDate', isoVal)}
            placeholder="mm/dd/yyyy"
          />
        </div>

        {/* Account Executive Filter */}
        {userRole !== 'account_executive' && userRole !== 'preparer' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Account Exec
            </label>
            <MultiSelectCheckbox
              label="Executives"
              options={aeOptions}
              selected={selectedAEs}
              onChange={handleAEChange}
              testId="filter-ae"
            />
          </div>
        )}

        {/* Service Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Service
          </label>
          <MultiSelectCheckbox
            label="Services"
            options={serviceOptions}
            selected={selectedServices}
            onChange={handleServiceChange}
            testId="filter-service"
          />
        </div>

        {/* State Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            State
          </label>
          <MultiSelectCheckbox
            label="States"
            options={stateOptions}
            selected={selectedStates}
            onChange={handleStateChange}
            testId="filter-state"
          />
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Status
          </label>
          <MultiSelectCheckbox
            label="Statuses"
            options={STATUS_OPTIONS}
            selected={selectedStatuses}
            onChange={handleStatusChange}
            testId="filter-status"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-end gap-2">
          <button
            onClick={onApplyFilters}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
            data-testid="apply-filters-btn"
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>
          <button
            onClick={onClearFilters}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="clear-filters-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
